/**
 * ListCalendarPanel — zakładka „Kalendarz" w Kreatorze Listy.
 *
 * Konfiguruje `list_config.calendarConfig`: pole daty startu (wymagane), opcjonalne
 * pole daty końca (zakres), pole statusu (kolor wpisu + legenda) oraz pole etykiety.
 * Zapis przez `useTableLayout(slug)` (zachowuje kolumny/sort/filtry/resztę configu).
 *
 * Źródło pól zależy od trybu (`dateFieldsOnly`, patrz prop):
 *  - kartoteki systemowe (`dateFieldsOnly`): kalendarz NIE zależy od widocznych kolumn listy, więc
 *    pola pochodzą z KATALOGU pól kartoteki (`ReferenceListContext.availableFields` — pola z „Pokaż
 *    na liście", nazwy i typy) oraz z kolumn, które lista rysuje spoza katalogu (Firma, Kategoria,
 *    szczegóły umowy, Nadawca… — rejestr kolumn), nie z kolumn układu. Pusty układ kolumn nie
 *    blokuje konfiguracji. „Pole daty startu" = pola katalogu typu data/czas, które serwer umie
 *    filtrować (kalendarz dociąga rekordy miesiąca filtrem zakresu po polu startu);
 *    „Pole daty końca" = pola katalogu typu data/czas (koniec rysuje zakres — jego wartość
 *    przychodzi projekcją niezależnie od filtrowalności; do filtra miesiąca serwer bierze je w
 *    parze z polem startu dowolnego rodzaju i klasy daty — kolumna podstawowa, pole dodatkowe,
 *    data albo data i czas — bez wymogu flagi „filtrowalne", `LIST_ENGINE.md`); listy dat zostają przy
 *    katalogu, bo typ pola jest znany tylko tam — kolumny rejestru nie mają typu i nie są
 *    oferowane jako daty. „Pole statusu" i „Pole etykiety" = WSZYSTKIE kolumny, które lista umie
 *    pokazać (`resolveListColumnSet` — to samo źródło co zakładka „Kolumny"); kolumna występująca
 *    pod dwoma id (pole katalogu `company_id` i kolumna rejestru `company`) jest pokazana raz,
 *    jako kolumna rejestru — to jej id zapisują układy, a runtime rozpoznaje oba;
 *  - moduły własne i szablony (bez `dateFieldsOnly`): jak dawniej — kolumny aktywnego layoutu (nazwy
 *    z katalogu pól, gdy podany, rejestr kolumn jako fallback — jak zakładka Kanban), bez filtra typów.
 *
 * Runtime kalendarza jest odporny na błędne pole startu: gdy pole nie niesie daty, wpis trafia
 * do paska „Brak daty".
 *
 * Zapisana wcześniej wartość spoza opcji NIE jest kasowana po cichu: stan panelu trzyma
 * zapisany slug i zapis zwraca go bez zmian. W trybie `dateFieldsOnly` każda z czterech list
 * pokazuje ją jako wyszarzoną (disabled) pozycję z nazwą pola i dopiskiem powodu („pole niedostępne
 * na liście" — pola nie ma wśród kolumn, które lista umie pokazać, „nie jest polem daty" — także
 * dla kolumny rejestru bez typu zapisanej jako start/koniec, a dla startu „pole bez filtrowania"),
 * żeby było widać, co jest ustawione. Zapisane id kolumny, którą lista umie pokazać (np. `company`,
 * `contract_status`), jest zwykłą, wybieralną pozycją — bez dopisku. Pozycja wynika z wartości
 * ZAPISANEJ w układzie (`layoutConfig.calendarConfig`), nie z bieżącego wyboru w panelu — wybranie
 * innego pola albo pozycji pustej jej nie usuwa z listy (znika dopiero po zapisie nowej
 * konfiguracji). Poza `dateFieldsOnly` (moduły własne, szablony) panel działa jak dawniej — bez tej
 * pozycji.
 *
 * Jedyny wyjątek od „zapis zwraca slug bez zmian": pole katalogu zastąpione kolumną strony
 * (`company_id` → `company`, `category_id` → `category`). Panel pokazuje je jako tę kolumnę — jedna
 * zwykła, wybrana pozycja, bez wyszarzonej „niedostępne na liście" obok niej — a zapis niesie id
 * kolumny. To ta sama zamiana, którą robi normalizacja układów listy (`replacementColumnId` z
 * `@saas/shared`, tu przez `ListColumnSet.canonicalColumnId`); runtime kalendarza rozpoznaje oba id,
 * więc wpis działa tak samo. Zamiana jest liczona przy każdym renderze ze stanu panelu, nie raz przy
 * wczytaniu układu — katalog pól może dojść później niż układ.
 *
 * Gdy żadne pole katalogu nie kwalifikuje się na pole STARTU (po wczytaniu katalogu), pod listami
 * dat pojawia się podpowiedź. Gdy katalog się nie wczytał (`catalogError`), wszystkie cztery listy
 * zastępuje komunikat błędu z ponowieniem — bez katalogu nie mamy ani pól, ani ich typów.
 *
 * Osadzenie w panelu ITE (edycja nazwanego widoku listy, `KartotekaViewListBuilder`;
 * `ReferenceListContext.catalogListFlagUnknown`): katalog to WSZYSTKIE pola definicji kartoteki,
 * bez flagi „Pokaż na liście" — definicja jej przy polach nie niesie, a domyślny układ listy z
 * serwera zawiera wyłącznie pola rdzenne (pola dodane i pula klasy, np. `date_from` w „specjalne",
 * w nim nie ma), więc nie da się wiarygodnie wskazać pól, których lista klienta nie narysuje.
 * ZNANE OGRANICZENIE: kalendarz w ITE może zatem zaoferować pole, którego lista klienta nie rysuje
 * (u klienta: start → „kalendarz nieskonfigurowany", koniec → wpis w jednym dniu, status → kolor
 * domyślny, etykieta → skrócone id wiersza). Zamiast zgadywać, panel pokazuje wtedy pod opisem
 * ogólną podpowiedź o opcji „Pokaż na liście"; opcje list pól zostają bez zmian. Naprawa wymaga
 * rozszerzenia API definicji o flagę (ten sam dług co brak `isFilterable`).
 */

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Save } from 'lucide-react';

import { Button } from '@/components/Button';
import { Dropdown, type DropdownOption } from '@/components/Dropdown';
import {
  type ReferenceListAvailableField,
  useReferenceListContext,
} from '@/contexts/ReferenceListContext';
import { useToast } from '@/contexts/ToastContext';
import { FieldCatalogErrorState, isDateFieldType } from '@/lib/ext-list-columns';

import { useColumnLabel } from './list-column-registry';
import { type ListColumnCandidate, resolveListColumnSet } from './list-column-set';
import { useTableLayout } from './useTableLayout';

export interface ListCalendarPanelProps {
  slug: string;
  /**
   * Włącza źródło pól z KATALOGU kartoteki (`ReferenceListContext.availableFields`) i z kolumn
   * rysowanych przez stronę spoza katalogu zamiast kolumn układu — kalendarz nie zależy od
   * widoczności kolumn listy. Opcje: „Pole daty startu" = pola katalogu typu data/czas
   * (`fieldType`) niewykluczone jako niefiltrowalne (`isFilterable !== false`); „Pole daty końca" =
   * pola katalogu typu data/czas (filtrowalność nie ma znaczenia); „Pole statusu" i „Pole
   * etykiety" = wszystkie kolumny, które lista umie pokazać (katalog + rejestr kolumn strony).
   *
   * Włączane wyłącznie dla kartotek systemowych (`scope='system'`), gdzie katalog niesie
   * typy. Świadomie WYŁĄCZONE (domyślnie) dla modułów własnych i szablonów: tam typ pola
   * nie jest znany w tym kontekście (osobny slug-space, katalog bez `fieldType`) — lista
   * zostaje jak dotąd, ze wszystkimi kolumnami układu; to zachowanie zamierzone, nie
   * zapasowe.
   *
   * Przy włączonym trybie BRAK typu = NIE data: pole katalogu bez `fieldType` nie jest oferowane
   * jako data, a przy takim polu nie pokazujemy też podpowiedzi „brak pól daty" (nie wiemy, czy to
   * nie data — podpowiedź byłaby fałszywa). Wyjątek: wbudowane pozycje katalogu kreatora (nazwa,
   * numer i kategoria rekordu: `record_name`, `record_number`, `category`) nie niosą typu, ale
   * wiadomo, że nie są datami — nie wyciszają podpowiedzi. Dopóki katalog się ładuje (`availableFields`
   * niezdefiniowane), wszystkie listy zawierają tylko pozycję pustą (plus ewentualną wyszarzoną
   * pozycję z zapisaną wartością).
   */
  dateFieldsOnly?: boolean;
  /**
   * Katalog pól nie wczytał się (błąd zapytania) — przy `dateFieldsOnly` wszystkie cztery listy są
   * zastępowane komunikatem błędu z przyciskiem ponowienia (`onCatalogRetry`). Wołający
   * ustawiający `catalogError` MUSI podać `onCatalogRetry` (brak = błąd programisty).
   */
  catalogError?: boolean;
  /** Ponowienie pobrania katalogu pól (przycisk w komunikacie błędu katalogu). */
  onCatalogRetry?: () => void;
}

/** Jedna opcja listy wyboru pola: slug (wartość zapisywana w `calendarConfig`) i nazwa do pokazania. */
interface FieldChoice {
  id: string;
  label: string;
}

type FieldRole = 'start' | 'end' | 'other';

/**
 * Wbudowane pozycje katalogu kartotek z kreatora (nazwa, numer i kategoria rekordu) — bez
 * `fieldType`, ale znane z nazwy: żadna nie jest datą, więc nie wyciszają podpowiedzi „brak pola
 * daty" (jej sprawdzenie szuka pozycji o NIEZNANYM typie). `KartotekaViewListBuilder` dokłada je
 * do katalogu zawsze.
 */
const BUILTIN_NON_DATE_IDS: ReadonlySet<string> = new Set([
  'record_name',
  'record_number',
  'category',
]);

export function ListCalendarPanel({
  slug,
  dateFieldsOnly = false,
  catalogError = false,
  onCatalogRetry,
}: ListCalendarPanelProps): React.ReactElement {
  const { t } = useTranslation();
  const toast = useToast();
  const { layout, isLoading, saveLayout, isSaving } = useTableLayout(slug);
  const columnLabel = useColumnLabel(slug);
  const referenceCtx = useReferenceListContext();
  const catalog = referenceCtx?.availableFields;

  const [startSlug, setStartSlug] = useState('');
  const [endSlug, setEndSlug] = useState('');
  const [statusSlug, setStatusSlug] = useState('');
  const [labelSlug, setLabelSlug] = useState('');

  useEffect(() => {
    const cc = layout?.layoutConfig?.calendarConfig;
    if (cc) {
      setStartSlug(cc.startFieldSlug ?? '');
      setEndSlug(cc.endFieldSlug ?? '');
      setStatusSlug(cc.statusFieldSlug ?? '');
      setLabelSlug(cc.labelFieldSlug ?? '');
    }
  }, [layout]);

  if (isLoading) return <p className="text-sm text-text-tertiary">…</p>;
  if (!layout) {
    return <p className="text-sm text-text-muted">{t('tenant.listBuilder.columns.noLayout')}</p>;
  }

  // Błąd katalogu (tylko przy filtrze dat): wymaga sposobu ponowienia. Brak handlera to
  // zepsuty kontrakt wołającego — rzucamy zamiast rysować martwy przycisk „Spróbuj ponownie".
  let catalogRetry: (() => void) | null = null;
  if (dateFieldsOnly && catalogError) {
    if (!onCatalogRetry) throw new Error('ListCalendarPanel: catalogError wymaga onCatalogRetry');
    catalogRetry = onCatalogRetry;
  }

  const columns = layout.layoutConfig.columns ?? [];
  const label = (id: string): string => columnLabel(id) ?? id;
  const hasColumns = columns.length > 0;
  // Wartości ZAPISANE w układzie — z nich wynika wyszarzona pozycja „zapisana wartość spoza opcji"
  // (nie z bieżącego wyboru w panelu, który użytkownik może zmieniać przed zapisem).
  const savedCalendar = layout.layoutConfig.calendarConfig;

  const catalogReady = catalog !== undefined;
  const catalogEntries: ReferenceListAvailableField[] = catalog ?? [];
  const catalogById = new Map<string, ReferenceListAvailableField>(
    catalogEntries.map((f) => [f.id, f]),
  );

  const columnChoices: FieldChoice[] = columns.map((c) => ({ id: c.id, label: label(c.id) }));
  const toChoice = (f: ReferenceListAvailableField): FieldChoice => ({ id: f.id, label: f.name });

  // Pola katalogu będące datą wg typu (data/czas). Poza `dateFieldsOnly` typ pola jest nieznany
  // (moduły własne / szablony) i nie filtrujemy.
  const dateEntries = catalogEntries.filter((f) => isDateFieldType(f.fieldType));

  // Wszystkie kolumny, które lista umie pokazać: katalog ∪ kolumny strony spoza katalogu (Firma,
  // Kategoria, szczegóły umowy…) — to samo źródło co zakładka „Kolumny". Kolumnę widoczną pod
  // dwoma id (pole katalogu `company_id` i kolumna rejestru `company`) pokazujemy raz — jako
  // kolumnę rejestru: jej id zapisują układy, a runtime rozpoznaje oba (`CalendarBoard` dopasowuje
  // po id kolumny i po slugu pola, `?columns=` po kluczach i wartościach mapy id → slug).
  // `undefined`, dopóki katalog się ładuje — wtedy nie ma czego oferować.
  const columnSet = resolveListColumnSet({ slug, referenceCtx, columnLabel, t });
  const listColumns: ListColumnCandidate[] = columnSet
    ? columnSet.candidates.filter((c) => !columnSet.replacedCatalogIds.has(c.id))
    : [];
  const listColumnIds = new Set(listColumns.map((c) => c.id));

  // Zapisany slug pola katalogu zastąpionego kolumną strony (`company_id` → `company`) liczymy jako
  // id tej kolumny — wtedy jest zwykłą, wybraną pozycją, a nie wyszarzoną „niedostępne na liście"
  // obok niej. Liczone przy każdym renderze (nie przy wczytaniu układu): katalog potrafi dojść
  // później niż układ. Poza `dateFieldsOnly` (moduły własne, szablony) bez zmian.
  const canonical = (id: string): string =>
    dateFieldsOnly && id && columnSet ? columnSet.canonicalColumnId(id) : id;
  const startValue = canonical(startSlug);
  const endValue = canonical(endSlug);
  const statusValue = canonical(statusSlug);
  const labelValue = canonical(labelSlug);

  // Kandydaci per rola:
  //  - koniec: dowolne pole daty — pole końca rysuje zakres, jego wartość przychodzi projekcją
  //    niezależnie od filtrowalności pola (do filtra miesiąca serwer dokłada je w parze z polem
  //    startu dowolnego rodzaju — kolumna podstawowa albo pole dodatkowe, data albo data i czas —
  //    i też bez wymogu flagi „filtrowalne": koniec nie jest filtrem nagłówka);
  //  - start: pole daty ORAZ filtrowalne serwerowo — kalendarz zawęża listę do miesiąca filtrem
  //    zakresu po polu startu (`isFilterable === false` wyklucza; brak flagi = nieznane, nie
  //    wyklucza);
  //  - status i etykieta: wszystkie kolumny, które lista umie pokazać (katalog + kolumny strony).
  // Daty startu i końca idą wyłącznie z katalogu — typ pola jest znany tylko tam (kolumny
  // rejestru nie mają typu, więc nie są oferowane jako daty).
  // Poza `dateFieldsOnly` wszystkie trzy listy oferują kolumny układu (jak dawniej).
  //
  // Wyjątek świadomie NIE scalany — Pracownicy mają dwie pozycje na tym samym polu bazy: `last_name`
  // z katalogu wskazuje kolumnę ZŁOŻONĄ o id `last_name` (nadpisanie w `Employees.tsx`) — kreator
  // podpisuje ją jak nagłówek listy, „Imię i nazwisko" (`kartotekaListFieldLabelKey` stosowane w
  // `resolveListColumnSet`); `surname` z rejestru to kolumna samego nazwiska, „Nazwisko". To różne
  // kolumny listy, nie duplikat do usunięcia: obie są na liście wyboru, każda pod swoim id.
  const startChoices: FieldChoice[] = dateFieldsOnly
    ? dateEntries.filter((f) => f.isFilterable !== false).map(toChoice)
    : columnChoices;
  const endChoices: FieldChoice[] = dateFieldsOnly ? dateEntries.map(toChoice) : columnChoices;
  const otherChoices: FieldChoice[] = dateFieldsOnly
    ? listColumns.map((c) => ({ id: c.id, label: c.name }))
    : columnChoices;

  // Podpowiedź o braku pola startu — tylko gdy filtr dat jest aktywny, katalog jest wczytany
  // (w trakcie ładowania każde pole wygląda jak nie-data) i żadne pole katalogu nie kwalifikuje się
  // na start. Nie pokazujemy jej, gdy jakieś pole katalogu jest BEZ typu — nie wiemy, czy to nie
  // data (wyjątek: wbudowane pozycje nazwa/numer/kategoria, o których wiadomo, że nie są datami).
  // Wariant „bez filtrowania": pola daty są, ale żadne nie nadaje się na start.
  const hasUnverifiableEntry = catalogEntries.some(
    (f) => f.fieldType === undefined && !BUILTIN_NON_DATE_IDS.has(f.id),
  );
  const showNoDateHint =
    dateFieldsOnly &&
    catalogRetry === null &&
    catalogReady &&
    startChoices.length === 0 &&
    !hasUnverifiableEntry;

  /** Dopisek powodu, dla którego zapisana wartość nie jest wśród opcji (pusty = bez dopisku). */
  const savedFieldNote = (saved: string, role: FieldRole): string => {
    // W trakcie ładowania katalogu nie twierdzimy nic o polu (ani o jego typie, ani o obecności).
    if (!catalogReady) return '';
    // Status i etykieta oferują wszystkie kolumny listy — zapisana wartość spoza opcji nie jest ani
    // polem katalogu, ani kolumną strony (pole katalogu zastąpione kolumną strony jest tu już
    // zamienione na jej id — `canonical`).
    if (role === 'other') return t('tenant.listBuilder.calendar.fieldNotInList');
    const entry = catalogById.get(saved);
    if (!entry) {
      // Kolumna strony spoza katalogu (np. `period`) jest na liście, ale nie ma typu — start i koniec
      // to pola o znanym typie daty. Wartość spoza kolumn listy w ogóle: nie ma jej na liście.
      return listColumnIds.has(saved)
        ? t('tenant.listBuilder.calendar.notDateField')
        : t('tenant.listBuilder.calendar.fieldNotInList');
    }
    if (!isDateFieldType(entry.fieldType)) return t('tenant.listBuilder.calendar.notDateField');
    // Filtrowalność liczy się tylko dla startu (koniec służy do rysowania zakresu).
    if (role === 'start' && entry.isFilterable === false) {
      return t('tenant.listBuilder.calendar.fieldNotFilterable');
    }
    return '';
  };

  /**
   * Opcje jednej z czterech list wyboru pola (rola: start / koniec / status i etykieta).
   * Zapisana w układzie wartość spoza kandydatów (pole niebędące datą, start bez filtrowania, pole
   * spoza katalogu) trafia na listę jako WYSZARZONA (disabled) pozycja z nazwą (katalog → rejestr →
   * slug) i dopiskiem powodu — trigger nie jest pusty i widać, co jest ustawione. Nic nie jest
   * kasowane po cichu: stan i zapis zachowują zapisany slug. W trakcie ładowania katalogu wartość
   * zapisana w układzie jest pokazana bez dopisku (nie twierdzimy nieprawdy). Tylko w trybie
   * `dateFieldsOnly` — poza nim (moduły własne, szablony) opcje są takie jak dawniej.
   */
  const buildOptions = (placeholder: string, role: FieldRole, saved: string): DropdownOption[] => {
    const choices = role === 'start' ? startChoices : role === 'end' ? endChoices : otherChoices;
    const options: DropdownOption[] = [
      { value: '', label: placeholder },
      ...choices.map((c) => ({ value: c.id, label: c.label })),
    ];
    // Poza filtrem dat (moduły własne, szablony) zachowanie jak dawniej — bez pozycji
    // zapisanej wartości (decyzja: moduły własne nie są ruszane).
    if (!dateFieldsOnly || !saved || choices.some((c) => c.id === saved)) return options;
    const note = savedFieldNote(saved, role);
    return [
      ...options,
      { value: saved, label: note ? `${label(saved)} ${note}` : label(saved), disabled: true },
    ];
  };

  const handleSave = async () => {
    if (!startValue) {
      toast.show({ variant: 'error', title: t('tenant.listBuilder.calendar.missingStart') });
      return;
    }
    await saveLayout({
      ...layout.layoutConfig,
      calendarConfig: {
        startFieldSlug: startValue,
        ...(endValue ? { endFieldSlug: endValue } : {}),
        ...(statusValue ? { statusFieldSlug: statusValue } : {}),
        ...(labelValue ? { labelFieldSlug: labelValue } : {}),
      },
    });
    toast.show({ variant: 'success', title: t('tenant.listBuilder.calendar.saved') });
  };

  return (
    <div className="space-y-6" data-testid="list-calendar-panel">
      <p className="text-sm text-text-tertiary">{t('tenant.listBuilder.calendar.intro')}</p>

      {/* Panel ITE: katalog bez flagi „Pokaż na liście" — nie wiemy, które pola klient narysuje
          (patrz JSDoc komponentu), więc zamiast zgadywać dajemy ogólną podpowiedź. */}
      {dateFieldsOnly && referenceCtx?.catalogListFlagUnknown === true && (
        <p
          role="note"
          className="rounded-lg border border-border-secondary bg-bg-secondary px-4 py-3 text-sm text-text-muted"
          data-testid="calendar-list-flag-note"
        >
          {t('tenant.listBuilder.calendar.listFlagUnknownHint')}
        </p>
      )}

      {/* Pusty układ kolumn blokuje konfigurację tylko poza katalogiem pól (moduły własne,
          szablony) — w kartotekach systemowych pola idą z katalogu, nie z kolumn układu. */}
      {!dateFieldsOnly && !hasColumns && (
        <p className="rounded-lg border border-border-secondary bg-bg-secondary px-4 py-3 text-sm text-text-muted">
          {t('tenant.listBuilder.calendar.noColumns')}
        </p>
      )}

      {catalogRetry ? (
        // Bez katalogu nie mamy pól ani ich typów — zamiast pustych list komunikat z ponowieniem.
        <FieldCatalogErrorState onRetry={catalogRetry} />
      ) : (
        <>
          {/* Pole daty startu (wymagane) */}
          <div className="space-y-1.5">
            <label htmlFor="calendar-start" className="block text-sm font-medium text-text-primary">
              {t('tenant.listBuilder.calendar.startField')}
            </label>
            <Dropdown
              id="calendar-start"
              options={buildOptions(
                t('tenant.listBuilder.calendar.selectField'),
                'start',
                canonical(savedCalendar?.startFieldSlug ?? ''),
              )}
              value={startValue}
              onChange={(val) => setStartSlug(val)}
              className="max-w-sm px-2 py-1.5"
              data-testid="calendar-start-select"
            />
            <p className="text-xs text-text-muted">
              {t('tenant.listBuilder.calendar.startFieldHint')}
            </p>
          </div>

          {/* Pole daty końca (opcjonalne — zakres) */}
          <div className="space-y-1.5">
            <label htmlFor="calendar-end" className="block text-sm font-medium text-text-primary">
              {t('tenant.listBuilder.calendar.endField')}
            </label>
            <Dropdown
              id="calendar-end"
              options={buildOptions(
                t('tenant.listBuilder.calendar.noField'),
                'end',
                canonical(savedCalendar?.endFieldSlug ?? ''),
              )}
              value={endValue}
              onChange={(val) => setEndSlug(val)}
              className="max-w-sm px-2 py-1.5"
              data-testid="calendar-end-select"
            />
            <p className="text-xs text-text-muted">
              {t('tenant.listBuilder.calendar.endFieldHint')}
            </p>
          </div>

          {/* Jedna podpowiedź (o polu STARTU) dla obu list dat, tuż pod nimi. */}
          {showNoDateHint && (
            <p
              role="status"
              className="rounded-lg border border-border-secondary bg-bg-secondary px-4 py-3 text-sm text-text-muted"
              data-testid="calendar-no-date-columns"
            >
              {dateEntries.length > 0
                ? t('tenant.listBuilder.calendar.noFilterableDateColumns')
                : t('tenant.listBuilder.calendar.noDateColumns')}
            </p>
          )}

          {/* Pole statusu (opcjonalne — kolor + legenda) */}
          <div className="space-y-1.5">
            <label
              htmlFor="calendar-status"
              className="block text-sm font-medium text-text-primary"
            >
              {t('tenant.listBuilder.calendar.statusField')}
            </label>
            <Dropdown
              id="calendar-status"
              options={buildOptions(
                t('tenant.listBuilder.calendar.noField'),
                'other',
                canonical(savedCalendar?.statusFieldSlug ?? ''),
              )}
              value={statusValue}
              onChange={(val) => setStatusSlug(val)}
              className="max-w-sm px-2 py-1.5"
              data-testid="calendar-status-select"
            />
            <p className="text-xs text-text-muted">
              {t('tenant.listBuilder.calendar.statusFieldHint')}
            </p>
          </div>

          {/* Pole etykiety wpisu (opcjonalne) */}
          <div className="space-y-1.5">
            <label htmlFor="calendar-label" className="block text-sm font-medium text-text-primary">
              {t('tenant.listBuilder.calendar.labelField')}
            </label>
            <Dropdown
              id="calendar-label"
              options={buildOptions(
                t('tenant.listBuilder.calendar.labelDefault'),
                'other',
                canonical(savedCalendar?.labelFieldSlug ?? ''),
              )}
              value={labelValue}
              onChange={(val) => setLabelSlug(val)}
              className="max-w-sm px-2 py-1.5"
              data-testid="calendar-label-select"
            />
            <p className="text-xs text-text-muted">
              {t('tenant.listBuilder.calendar.labelFieldHint')}
            </p>
          </div>
        </>
      )}

      <div className="flex justify-end">
        <Button
          variant="primary"
          size="sm"
          leftIcon={<Save size={16} aria-hidden="true" />}
          onClick={handleSave}
          disabled={isSaving || !startSlug}
          data-testid="calendar-save"
        >
          {isSaving
            ? t('tenant.listBuilder.calendar.saving')
            : t('tenant.listBuilder.calendar.saveBtn')}
        </Button>
      </div>
    </div>
  );
}
